<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <InfoTooltip
        v-if="!isPap"
        :text="$t('forwards.mandates.volumes.intro')"
        class="text-body-tertiary fs-9"
        >{{ $t("forwards.mandates.volumes.how_it_works") }}</InfoTooltip
      >
      <LoadingIndicatorComponent
        v-if="previewLoading"
        inline
        :message="$t('forwards.mandates.volumes.previewing')"
      />
    </div>
    <div v-if="!isPap && !simple" class="mb-3">
      <span class="eyebrow-label eyebrow-label--sm d-block mb-1">{{
        $t("forwards.mandates.volumes.mode")
      }}</span>
      <SegmentedControl v-model="modeValue" :options="modeOptions" />
    </div>

    <div v-if="!simple" class="mb-2">
      <span class="eyebrow-label eyebrow-label--sm d-block mb-1"
        >{{ $t("forwards.mandates.volumes.entry_width") }}
        <InfoTooltip
          v-if="!isPap"
          :text="$t('forwards.mandates.volumes.entry_width_hint')"
      /></span>
      <SegmentedControl v-model="widthValue" :options="widthOptions" />
    </div>
    <p v-if="listedWidth === 'month'" class="advisory-note mb-3">
      <FontAwesomeIcon icon="fa-regular fa-circle-info" />
      <span>{{ $t("forwards.mandates.volumes.month_note") }}</span>
    </p>
    <div
      v-if="listedWidth === 'month'"
      class="alert alert-warning mb-3"
      role="alert"
    >
      {{ $t("forwards.mandates.volumes.month_warning") }}
    </div>
    <p v-if="listedWidth === 'half_year'" class="advisory-note mb-3">
      <FontAwesomeIcon icon="fa-regular fa-circle-info" />
      <span>{{ $t("forwards.mandates.volumes.half_year_note") }}</span>
    </p>
    <p v-if="listedWidth === 'year'" class="advisory-note mb-3">
      <FontAwesomeIcon icon="fa-regular fa-circle-info" />
      <span>{{ $t("forwards.mandates.volumes.year_note") }}</span>
    </p>
    <p v-if="isPap" class="advisory-note mb-3">
      <FontAwesomeIcon icon="fa-regular fa-circle-info" />
      <span>{{ $t("forwards.mandates.volumes.pay_as_produced_note") }}</span>
    </p>

    <template v-if="!isPap">
      <table class="table table-sm align-middle volumes-table">
        <thead>
          <tr>
            <th scope="col">{{ $t("forwards.mandates.volumes.period") }}</th>
            <th scope="col" class="text-end">
              {{ $t("forwards.common.mwh") }}
            </th>
            <th scope="col" class="text-end">
              {{ $t("forwards.mandates.volumes.share_of_estimate") }}
            </th>
            <th scope="col">{{ $t("forwards.mandates.volumes.feedback") }}</th>
            <th v-if="listingKind === 'one_off'" scope="col">
              <span class="visually-hidden">{{
                $t("forwards.common.actions")
              }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.key">
            <td>
              <span v-if="listingKind === 'recurring'">{{ row.label }}</span>
              <template v-else>
                <select
                  :value="row.label"
                  class="form-select form-select-sm period-select"
                  :class="{ 'is-invalid': row.periodError !== null }"
                  :disabled="disabled"
                  :aria-label="$t('forwards.mandates.volumes.period')"
                  @change="onPeriodSelect(row, $event)"
                >
                  <option
                    v-for="label in periodChoicesFor(row)"
                    :key="label"
                    :value="label"
                    :disabled="label !== row.label && listedLabels.has(label)"
                  >
                    {{ label }}
                  </option>
                </select>
                <div class="invalid-feedback">{{ row.periodError }}</div>
              </template>
            </td>
            <td class="text-end">
              <MwhInputComponent
                :modelValue="row.volume"
                size="sm"
                :disabled="disabled"
                :ariaLabel="
                  $t('forwards.mandates.volumes.mwh_for', { period: row.label })
                "
                @update:modelValue="setVolume(row, $event)"
              />
            </td>
            <td class="text-end">
              <MwhInputComponent
                :modelValue="row.share"
                unit="%"
                size="sm"
                :step="0.1"
                :disabled="disabled || row.estimated === 0"
                :ariaLabel="
                  $t('forwards.mandates.volumes.share_for', {
                    period: row.label,
                  })
                "
                @update:modelValue="setShare(row, $event)"
              />
            </td>
            <td>
              <span class="text-body-tertiary fs-10">{{ row.feedback }}</span>
              <span
                v-if="row.warning"
                class="badge rounded-pill bg-warning-subtle text-warning-emphasis ms-1"
              >
                {{ row.warning }}
              </span>
            </td>
            <td v-if="listingKind === 'one_off'" class="text-end">
              <button
                type="button"
                class="btn btn-sm btn-link text-danger p-0"
                :disabled="disabled"
                :aria-label="
                  $t('forwards.mandates.volumes.remove_period', {
                    period: row.label,
                  })
                "
                @click="removeListing(row.index)"
              >
                <FontAwesomeIcon icon="fa-solid fa-xmark" />
              </button>
            </td>
          </tr>
          <tr v-if="rows.length === 0">
            <td
              :colspan="listingKind === 'one_off' ? 5 : 4"
              class="text-body-tertiary text-center py-3"
            >
              {{ $t("forwards.mandates.volumes.no_rows") }}
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="showFreeHint" class="text-body-tertiary fs-10 mb-2">
        {{ $t("forwards.mandates.volumes.free_hint") }}
      </p>

      <button
        v-if="listingKind === 'one_off'"
        type="button"
        class="btn btn-ghost btn-sm"
        :disabled="disabled || nextFreePeriod === null"
        @click="addListing"
      >
        <FontAwesomeIcon icon="fa-solid fa-plus" class="me-1" />
        {{ $t("forwards.mandates.volumes.add_period") }}
      </button>
    </template>

    <div
      v-if="listingChanged && unmarkedOverCommitted.length > 0"
      class="alert alert-info mt-3 mb-0"
      role="status"
      data-testid="over-committed-note"
    >
      <p class="mb-1">
        {{ $t("forwards.mandates.volumes.over_committed_note") }}
      </p>
      <ul class="mb-0">
        <li v-for="line in unmarkedOverCommitted" :key="line">{{ line }}</li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import SegmentedControl, {
  type SegmentedControlOption,
} from "@/components/common/SegmentedControl.vue";
import {
  formatDateTimeIntoIsoDate,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import type {
  ListingWrite,
  PeriodPreview,
  MandateListingKind,
  MandatePreviewResponse,
} from "@/forwards2/forwardsTypes";
import {
  currentPeriodLabel,
  periodEndDate,
  periodLabelOfBounds,
  periodLabelsOfYears,
  periodStartDate,
  recurringSlotCount,
  recurringSlotOf,
  type PeriodWidth,
} from "@/forwards2/periodUtils";
import { recurringSlotLabel } from "@/forwards2/mandates/mandateListingView";
import {
  listingRowErrors,
  type ListingRowError,
} from "@/forwards2/mandates/mandateFormRules";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The mandate's listed volumes.
 *
 * Two shapes, exclusive by construction on the backend: a recurring mandate states one volume per slot
 * of the year (four quarters, two half-years, twelve months, or one whole year); a one-off mandate
 * states a volume per named period. A pay-as-produced mandate
 * states neither; the width control offers it as its last option.
 *
 * The kind and the width are chosen by the parent, which keeps each mode's entries apart: the models
 * below are the selected mode's, and switching modes swaps them rather than clearing them. A width
 * picked while pay-as-produced is emitted like any other; the parent knows which kind it returns to.
 *
 * The percentage column is the same volume expressed against what the mandate's devices are expected
 * to produce in that period, net of PPA. That estimate only ever comes from `POST /mandates/preview` —
 * the conversion is arithmetic, but the number behind it is not something a client can work out.
 * For a recurring slot the estimate of the earliest period of that slot still wholly in the future is
 * used, since one slot stands for one period per year and the estimate of a period already in
 * production is only what is left of it.
 *
 * Every field takes what the user types, including values the mandate cannot be saved with: those are
 * marked and explained here (`mandateFormRules`), and the store reads the same rules to hold back the
 * preview and the save.
 */
const props = withDefaults(
  defineProps<{
    listingKind: MandateListingKind;
    entryWidth: PeriodWidth;
    preview: MandatePreviewResponse | null;
    previewLoading?: boolean;
    disabled?: boolean;
    /**
     * The quarters of a recurring listing and nothing else: no kind or width to choose (`mandateListingView.usesSimpleListing`). The parent only asks for it while the listing is
     * that shape and staff have not chosen the advanced editor for the mandate.
     */
    simple?: boolean;
    /** The listing differs from the saved mandate's, so the over-commitment note applies. */
    listingChanged?: boolean;
  }>(),
  {
    previewLoading: false,
    disabled: false,
    simple: false,
    listingChanged: false,
  }
);

const emit = defineEmits<{
  "select:kind": [kind: MandateListingKind];
  "select:width": [width: PeriodWidth];
}>();
const recurringVolumes = defineModel<Record<string, number>>(
  "recurringVolumes",
  { required: true }
);
const listings = defineModel<ListingWrite[]>("listings", { required: true });

const { t } = useI18n();

/** A one-off period is picked from the current year and this many years after it. */
const PERIOD_CHOICE_YEARS_AHEAD = 3;

// Production can fall short of its estimate, so a listing near the estimate is already a risk
// (user decision, 2026-10-20).
const UTILISATION_WARNING_SHARE = 0.8;

type ListedKind = Exclude<MandateListingKind, "pay_as_produced">;
/** What the width control offers: the four widths, and pay-as-produced, which states none. */
type WidthChoice = PeriodWidth | "pay_as_produced";

const isPap = computed(() => props.listingKind === "pay_as_produced");
/** The width the mandate lists at, or null for a pay-as-produced mandate, which lists nothing. */
const listedWidth = computed<PeriodWidth | null>(() =>
  isPap.value ? null : props.entryWidth
);

interface EnteredShare {
  value: number | null;
  volume: number | null;
  estimated: number;
  label: string;
}

// Whole-MWh rounding is lossy: 50% of 501 MWh becomes 251 MWh, then 50.1% on conversion back.
const enteredShares = ref<Record<string, EnteredShare>>({});

interface VolumeRow {
  key: string;
  /** Slot number for a recurring mandate, index into `listings` for a one-off one. */
  index: number;
  /** The period the row stands for: a recurring slot's name, a one-off row's picked period. */
  label: string;
  periodError: string | null;
  volume: number | null;
  share: number | null;
  estimated: number;
  feedback: string;
  warning: string | null;
  /** The previewed period whose figures the row shows, if the preview has it. */
  previewPeriod: string | null;
  /** Lists more than is committed yet shows nothing free — the case the free hint explains. */
  freeWithheld: boolean;
}

/** The arguments of {@link buildRow}, which differ between a recurring slot and a one-off row. */
interface RowInput {
  key: string;
  index: number;
  label: string;
  periodError?: string | null;
  volume: number | null;
  period: PeriodPreview | null;
}

const modeOptions = computed<SegmentedControlOption<ListedKind>[]>(() => [
  { value: "one_off", label: t("forwards.mandates.listing_kind.one_off") },
  { value: "recurring", label: t("forwards.mandates.listing_kind.recurring") },
]);

const widthOptions = computed<SegmentedControlOption<WidthChoice>[]>(() => [
  { value: "quarter", label: t("forwards.mandates.entry_width.quarter") },
  { value: "month", label: t("forwards.mandates.entry_width.month") },
  {
    value: "half_year",
    label:
      props.preview?.recommended_entry_width === "half_year"
        ? t("forwards.mandates.entry_width.half_year_recommended")
        : t("forwards.mandates.entry_width.half_year"),
  },
  { value: "year", label: t("forwards.mandates.entry_width.year") },
  {
    value: "pay_as_produced",
    label: t("forwards.mandates.listing_kind.pay_as_produced"),
  },
]);

const modeValue = computed<ListedKind>({
  get: () => (props.listingKind === "recurring" ? "recurring" : "one_off"),
  set: (value) => emit("select:kind", value),
});

const widthValue = computed<WidthChoice>({
  get: () => listedWidth.value ?? "pay_as_produced",
  set: (value) =>
    value === "pay_as_produced"
      ? emit("select:kind", "pay_as_produced")
      : emit("select:width", value),
});

/** Typed shares are kept per mode, like the volumes they were typed against. */
const shareKeyPrefix = computed(
  () => `${props.listingKind}-${props.entryWidth}-`
);

const previewByLabel = computed(
  () =>
    new Map(
      (props.preview?.periods ?? []).map((period) => [
        period.period_label,
        period,
      ])
    )
);

const rows = computed<VolumeRow[]>(() =>
  props.listingKind === "recurring" ? recurringRows() : oneOffRows()
);

// Free is measured as the picker sells today, so an ended period shows 0 next to volume it still
// lists; without a word that zero reads as a bug.
const showFreeHint = computed(() => rows.value.some((row) => row.freeWithheld));

// A row's feedback line already states what its period has committed: a backed period whose row was deleted, a mandate retyped to
// pay-as-produced, or a recurring slot's later years would otherwise go unmentioned until the save.
// Listing less than was committed is valid (a mandate cut back for future deals), so the note speaks
// only to a listing being edited (user decision, 2026-10-09).
const unmarkedOverCommitted = computed(() => {
  const shown = new Set(
    isPap.value ? [] : rows.value.map((row) => row.previewPeriod)
  );
  return (props.preview?.over_committed ?? [])
    .filter((period) => !shown.has(period.period_label))
    .map((period) =>
      t("forwards.mandates.volumes.over_committed_period", {
        period: period.period_label,
        committed: formatNumberWithSpaces(period.committed),
      })
    );
});

/** The periods a one-off row can be set to, at the mandate's entry width. */
const periodChoices = computed(() => {
  const year = new Date().getFullYear();
  return periodLabelsOfYears(
    props.entryWidth,
    year,
    year + PERIOD_CHOICE_YEARS_AHEAD
  );
});

const listedLabels = computed(
  () =>
    new Set(
      listings.value.map((listing) =>
        periodLabelOfBounds(listing.period_start, listing.period_end)
      )
    )
);

// Today's period first, so a new row starts where selling does; an earlier one of this year after.
const nextFreePeriod = computed(() => {
  const current = currentPeriodLabel(props.entryWidth);
  const free = periodChoices.value.filter(
    (label) => !listedLabels.value.has(label)
  );
  return free.find((label) => label >= current) ?? free[0] ?? null;
});

function recurringRows(): VolumeRow[] {
  const slots = recurringSlotCount(props.entryWidth);
  return Array.from({ length: slots }, (_unused, offset) => {
    const slot = offset + 1;
    return buildRow({
      key: `${shareKeyPrefix.value}slot-${slot}`,
      index: slot,
      label: slotLabel(slot),
      volume: recurringVolumes.value[String(slot)] ?? null,
      period: earliestPreviewForSlot(slot),
    });
  });
}

function oneOffRows(): VolumeRow[] {
  const errors = listingRowErrors(listings.value);
  return listings.value.map((listing, index) => {
    const label = periodLabelOfBounds(listing.period_start, listing.period_end);
    return buildRow({
      key: `${shareKeyPrefix.value}listing-${index}`,
      index,
      label: label ?? listing.period_start,
      periodError: periodErrorMessage(errors.get(index)),
      volume: listing.volume_mwh,
      period: label ? (previewByLabel.value.get(label) ?? null) : null,
    });
  });
}

function periodErrorMessage(error: ListingRowError | undefined): string | null {
  if (error === "duplicate")
    return t("forwards.mandates.volumes.errors.period_duplicate");
  return null;
}

function buildRow(input: RowInput): VolumeRow {
  const estimated = input.period?.estimated_mwh ?? 0;
  const volume = input.volume;
  const entered = enteredShares.value[input.key];
  const share =
    entered &&
    entered.volume === volume &&
    entered.estimated === estimated &&
    entered.label === input.label
      ? entered.value
      : estimated > 0 && volume !== null
        ? Math.round((volume / estimated) * 1000) / 10
        : null;
  return {
    key: input.key,
    index: input.index,
    label: input.label,
    periodError: input.periodError ?? null,
    volume,
    share,
    estimated,
    feedback: feedbackFor(input.period),
    warning: warningFor(input.period),
    previewPeriod: input.period?.period_label ?? null,
    freeWithheld:
      input.period !== null &&
      input.period.free === 0 &&
      input.period.listed > input.period.committed,
  };
}

function feedbackFor(period: PeriodPreview | null): string {
  if (!period) return t("forwards.mandates.volumes.no_estimate");
  return t("forwards.mandates.volumes.feedback_line", {
    committed: formatNumberWithSpaces(period.committed),
    free: formatNumberWithSpaces(period.free),
    estimated: formatNumberWithSpaces(period.estimated_mwh),
  });
}

function warningFor(period: PeriodPreview | null): string | null {
  if (!period) return null;
  if (
    period.utilisation !== null &&
    period.utilisation > UTILISATION_WARNING_SHARE
  ) {
    return t("forwards.mandates.volumes.over_estimate", {
      percent: Math.round(period.utilisation * 100),
    });
  }
  return null;
}

/** One recurring slot stands for one period per covered year; the earliest is what the row shows. */
function earliestPreviewForSlot(slot: number): PeriodPreview | null {
  const matching = (props.preview?.periods ?? []).filter(
    (period) => recurringSlotOf(period.period_label, props.entryWidth) === slot
  );
  // The estimate is net of volume already issued, so a period that has started reads as a
  // fraction of a year's production; a template states one volume per year, so price it against
  // the first period still wholly ahead and fall back to the earliest only when there is none.
  const today = formatDateTimeIntoIsoDate(new Date());
  const upcoming = matching.filter((period) => {
    const start = periodStartDate(period.period_label);
    return start !== null && start > today;
  });
  if (upcoming.length > 0) return upcoming[0];
  return matching.length > 0 ? matching[0] : null;
}

function slotLabel(slot: number): string {
  return recurringSlotLabel(props.entryWidth, slot, t);
}

function setVolume(row: VolumeRow, value: number | null) {
  delete enteredShares.value[row.key];
  if (props.listingKind === "recurring") {
    const next = { ...recurringVolumes.value };
    if (value === null) delete next[String(row.index)];
    else next[String(row.index)] = value;
    recurringVolumes.value = next;
    return;
  }
  listings.value = listings.value.map((listing, index) =>
    index === row.index ? { ...listing, volume_mwh: value ?? 0 } : listing
  );
}

function setShare(row: VolumeRow, value: number | null) {
  if (row.estimated === 0) return;
  const volume =
    value === null ? null : Math.round((value / 100) * row.estimated);
  setVolume(row, volume);
  enteredShares.value[row.key] = {
    value,
    volume: volume ?? (props.listingKind === "one_off" ? 0 : null),
    estimated: row.estimated,
    label: row.label,
  };
}

/**
 * The choices for one row. A saved row outside the offered years is kept as its first choice, so
 * opening a mandate never shows it as something it is not.
 */
function periodChoicesFor(row: VolumeRow): string[] {
  return periodChoices.value.includes(row.label)
    ? periodChoices.value
    : [row.label, ...periodChoices.value];
}

function onPeriodSelect(row: VolumeRow, event: Event) {
  const label = (event.target as HTMLSelectElement).value;
  const start = periodStartDate(label);
  const end = periodEndDate(label);
  if (start === null || end === null) return;
  delete enteredShares.value[row.key];
  listings.value = listings.value.map((listing, index) =>
    index === row.index
      ? { period_start: start, period_end: end, volume_mwh: listing.volume_mwh }
      : listing
  );
}

function addListing() {
  const label = nextFreePeriod.value;
  const start = label === null ? null : periodStartDate(label);
  const end = label === null ? null : periodEndDate(label);
  if (start === null || end === null) return;
  listings.value = [
    ...listings.value,
    { period_start: start, period_end: end, volume_mwh: 0 },
  ];
}

// Removing a row renumbers the ones after it, so this mode's typed shares no longer match their rows.
function removeListing(index: number) {
  enteredShares.value = Object.fromEntries(
    Object.entries(enteredShares.value).filter(
      ([key]) => !key.startsWith(shareKeyPrefix.value)
    )
  );
  listings.value = listings.value.filter(
    (_unused, position) => position !== index
  );
}
</script>

<style scoped lang="scss">
.volumes-table th {
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--phoenix-gray-500);
  font-weight: 700;
}

.period-select {
  max-width: 8rem;
}

// The message is a sentence in the narrowest column of the table; let it wrap rather than
// stretching the column.
.period-select + .invalid-feedback {
  max-width: 12rem;
}
</style>
